import {
  Box,
  Paper,
  Typography,
} from "@mui/material";
import { startOfToday } from "date-fns";
import { useState } from "react";

import { Availability } from "@caredna-marlin/utils/client/availability";
import EditAvailability from "./EditAvailability";

import { AvailabilityByTimespanQuery, useAvailabilityByTimespanQuery } from "~/graphql/__generated__/graphql";
import ProviderTable from "./ProviderTable";
import PermissionOrWarn from "~/components/common/PermissionOrWarn";
import { Permission, usePermissionOrRedirect } from "~/hooks/permissions";
import ProviderAvailabiltyMenu from "./ProviderAvailabiltyMenu";
import { AvailabilityAction } from "~/hooks/useAvailabilityOperation";
import { gql } from "@apollo/client";

const getManualDays = (manuallyEditedShifts: AvailabilityByTimespanQuery["shift"], manuallyEditedCalendarBlocks: AvailabilityByTimespanQuery["calendarBlock"]) => {
  const days: Record<string, {providerIds: number[], availabilityIds: number[]}> = {};

  manuallyEditedShifts.forEach(shift => {
    const shiftStart = (new Date(shift.startTime)).toDateString();
    days[shiftStart] = days[shiftStart] || { providerIds: [], availabilityIds: [] };
    days[shiftStart].providerIds.push(shift.providerId);
  });

  manuallyEditedCalendarBlocks.forEach(block => {
    if (!block.availabilityId) { return; }
    const blockStart = (new Date(block.startTime)).toDateString();
    days[blockStart] = days[blockStart] || { providerIds: [], availabilityIds: [] };
    days[blockStart].availabilityIds.push(block.availabilityId);
  });

  return days;
}

const AvailabilityManagement = () => {
  usePermissionOrRedirect(Permission.manageProviderShifts, "/command")

  const { data } = useAvailabilityByTimespanQuery({ variables: {
    start: startOfToday().toISOString(),
  }});

  const [editing, setEditing] = useState<null | { action: AvailabilityAction, avail: Availability }>(null);

  // An object like { "Jan 1": { providerIds: [1, 2], availabilityIds: [2, 3] }} where the providerIds are those that
  // have manually edited shift on that day, and availabilityIds are those availabilities that have a manually edited calendar block.
  // When a given day is selected, we warn the user that the availability system won't edit shifts / blocks on that day.
  const manualDays = getManualDays(data?.shift || [], data?.calendarBlock || []);

  const provider = data?.provider.find(p => p.id === editing?.avail.providerId);

  return <PermissionOrWarn permission={Permission.manageProviderShifts}>
    <Box sx={{ height: "100%", width: "100%", overflow: "hidden", mt: 4, mb: 4, ml: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Provider Availability
        <span style={{ marginLeft: 24 }}>
          <ProviderAvailabiltyMenu />
        </span>
      </Typography>
      <Paper sx={{ width: "100%", p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
        <ProviderTable data={data} setEditing={setEditing} />
      </Paper>

      {editing && provider && <EditAvailability
        action={editing.action}
        availability={editing.avail}
        onClose={() => setEditing(null)}
        provider={provider}
        manualDays={manualDays}
        selectedDate={startOfToday()}
      />}
    </Box>
  </PermissionOrWarn>
}

export default AvailabilityManagement;

gql(`
  query AvailabilityByTimespan($start: String!)
  @permission(requires: manageProviderShifts)
  {
    availability(where: {
      rruleUntil: { OR: [{ isNull: true }, { gte: $start }] }
    }) {
      id
      type
      note
      startTime
      endTime
      providerId

      providerTravel
      shiftCenterId
      overflowShift
      useShiftCenterDriveTimes

      blockType
      blockFilters {
        visitTypeIds
      }
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      addressTimezone
      addressCoordinates

      rruleFreq
      rruleDtstart
      rruleUntil
      rruleInterval
      rruleByweekday
    }

    provider(where: { active: { eq: true } }) {
      id
      addressTimezone
      firstName
      lastName
      providerTypeId
      addressStreet
      addressCity
      addressState

      teamProviders {
        teamId
      }

      serviceLineProviders {
        serviceLineId
      }

      organizationUserId
    }

    shift(where: {
      startTime: { gte: $start }
      manualEdits: { eq: true }
    }) {
      id
      addressTimezone
      providerId
      startTime
    }

    calendarBlock(where: {
      OR: [{
        startTime: { gte: $start }
        availabilityId: { isNull: false }
        manualEdits: { eq: true }
      },
      {
        startTime: { gte: $start }
        availabilityId: { isNull: false }
        eventStatus: { eq: canceled }
      }]
    }) {
      id
      startTime
      availabilityId
    }

    team {
      id
      name
    }
  }
`);
